import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { EDUGATE_API_URL } from '../../config/serviceConfig'
import { buildAccentThemeStyle } from '../../utils/brandTheme'
import CategoryView from './CategoryView'
import CourseSwitcher from './CourseSwitcher'
import PathMapView from './PathMapView'
import PathView from './PathView'
import PortalGuidePanel from './PortalGuidePanel'
import PortalSummary from './PortalSummary'
import TrialBanner from './TrialBanner'
import { studentIdOf } from './constants'
import './CoursePortal.css'

const PATH_PRESENTATION_KEY = 'ybg.coursePortal.pathPresentation'

function readPathPresentation() {
  try {
    const saved = window.localStorage?.getItem(PATH_PRESENTATION_KEY)
    if (saved === 'list' || saved === 'map') return saved
  } catch (_) { /* ignore */ }
  return 'map'
}

const DEFAULT_PORTAL_TABS = {
  ccma: true,
  plaza: true,
  groupchat: true,
  discuss: true,
  youlaber: true,
  drawing: true,
}

function normalizePortalTabs(rawFlags) {
  const tabs = { ...DEFAULT_PORTAL_TABS }
  const incoming = rawFlags?.tabs || {}
  Object.keys(tabs).forEach((key) => {
    if (typeof incoming[key] === 'boolean') tabs[key] = incoming[key]
  })
  return tabs
}

/** 多门课聚合：任一课程关闭某 Tab，则对学生隐藏（限制优先） */
export function aggregatePortalTabs(courses) {
  const tabs = { ...DEFAULT_PORTAL_TABS }
  ;(courses || []).forEach((course) => {
    const courseTabs = normalizePortalTabs(course?.featureFlags)
    Object.keys(tabs).forEach((key) => {
      if (!courseTabs[key]) tabs[key] = false
    })
  })
  return tabs
}

export default function CoursePortal({
  currentUser,
  onLaunch,
  onOpenCourseManagement,
  onOpenHomework,
  onOpenEvaluation,
  onOpenGrowth,
  onLoadFatal,
  onFeatureFlagsChange,
  onHideTrainingTabsChange,
  guideAgent,
  guideWelcomeContent,
  guideDocLinks,
  guideExpertServiceUrl,
  onGuideA2UIAction,
  guideSpotlight = false,
  guideFocusToken = 0,
}) {
  const studentId = studentIdOf(currentUser)
  const [courses, setCourses] = useState([])
  const [courseId, setCourseId] = useState('')
  const [portal, setPortal] = useState(null)
  const [view, setView] = useState('path')
  const [pathPresentation, setPathPresentation] = useState(readPathPresentation)
  const [loading, setLoading] = useState(true)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState('')
  const fatalNotifiedRef = useRef(false)
  const onLoadFatalRef = useRef(onLoadFatal)
  const onFeatureFlagsRef = useRef(onFeatureFlagsChange)
  const onHideTabsRef = useRef(onHideTrainingTabsChange)

  useEffect(() => {
    onLoadFatalRef.current = onLoadFatal
  }, [onLoadFatal])

  useEffect(() => {
    onFeatureFlagsRef.current = onFeatureFlagsChange
  }, [onFeatureFlagsChange])

  useEffect(() => {
    onHideTabsRef.current = onHideTrainingTabsChange
  }, [onHideTrainingTabsChange])

  const notifyFatal = useCallback((message) => {
    if (fatalNotifiedRef.current) return
    fatalNotifiedRef.current = true
    if (typeof onLoadFatalRef.current === 'function') {
      onLoadFatalRef.current(message)
    }
  }, [])

  const loadDetail = useCallback(async (selectedCourseId) => {
    if (!studentId) {
      setPortal(null)
      return
    }
    const params = new URLSearchParams({ student_id: String(studentId) })
    if (selectedCourseId) params.set('course_id', String(selectedCourseId))
    const response = await fetch(`${EDUGATE_API_URL}/api/course-portal/detail?${params}`)
    const result = await response.json()
    if (!response.ok || result.code !== 0) throw new Error(result.message || '加载课程入口失败')
    setPortal(result.data)
    setView(result.data?.course?.defaultView || 'path')
  }, [studentId])

  const refresh = useCallback(async (preferredCourseId, { soft = false } = {}) => {
    if (!studentId) {
      setLoading(false)
      setPortal(null)
      return
    }
    if (soft) {
      setSwitching(true)
    } else {
      setLoading(true)
    }
    setError('')
    try {
      const response = await fetch(`${EDUGATE_API_URL}/api/course-portal/courses?student_id=${encodeURIComponent(studentId)}`)
      const result = await response.json()
      if (!response.ok || result.code !== 0) throw new Error(result.message || '加载课程失败')
      const nextCourses = result.data || []
      setCourses(nextCourses)
      const tabs = aggregatePortalTabs(nextCourses)
      const hideTrainingTabs = !tabs.ccma && !tabs.plaza && !tabs.groupchat
      if (typeof onFeatureFlagsRef.current === 'function') {
        onFeatureFlagsRef.current({ tabs })
      }
      if (typeof onHideTabsRef.current === 'function') {
        onHideTabsRef.current(hideTrainingTabs)
      }
      const selected = nextCourses.find((course) => String(course.id) === String(preferredCourseId)) || nextCourses[0]
      const nextCourseId = selected?.id || ''
      setCourseId(nextCourseId)
      await loadDetail(nextCourseId)
      fatalNotifiedRef.current = false
    } catch (loadError) {
      const message = loadError.message || '加载课程入口失败'
      setError(message)
      notifyFatal(message)
    } finally {
      setLoading(false)
      setSwitching(false)
    }
  }, [loadDetail, notifyFatal, studentId])

  useEffect(() => {
    refresh('')
  }, [studentId]) // eslint-disable-line react-hooks/exhaustive-deps

  const groupedByCategory = useMemo(() => {
    const groups = {}
    ;(portal?.items || []).forEach((item) => {
      ;(groups[item.assetType] ||= []).push(item)
    })
    return groups
  }, [portal])

  const isBuptCas = currentUser?.user_type === 'bupt_cas'
  const isExperiencePortal = Boolean(portal?.experience)
  const isTrial = portal?.mode === 'trial'
  /** 正式课已接入 quests：零关卡不回退 items，避免把自由练习资源误认成必做关卡 */
  const useQuestsPath = Array.isArray(portal?.quests) && !isExperiencePortal && !isTrial
  const pathItems = useMemo(() => {
    if (useQuestsPath) return portal.quests
    if (Array.isArray(portal?.quests) && portal.quests.length > 0) return portal.quests
    return portal?.items || []
  }, [portal, useQuestsPath])

  const groupedByPath = useMemo(() => {
    const groups = new Map()
    pathItems.forEach((item) => {
      const number = Number(item.sectionNo) || 0
      if (!groups.has(number)) {
        groups.set(number, { no: number, title: item.sectionTitle || '', items: [] })
      }
      const group = groups.get(number)
      if (!group.title && item.sectionTitle) group.title = item.sectionTitle
      group.items.push(item)
    })
    // 未分组(0)排最后；其余按阶段号升序
    return [...groups.values()].sort((a, b) => {
      if (a.no === 0 && b.no !== 0) return 1
      if (b.no === 0 && a.no !== 0) return -1
      return a.no - b.no
    })
  }, [pathItems])

  const isPathZeroQuests = useQuestsPath && pathItems.length === 0
  const canJoinCourse = Boolean(portal?.canJoinCourse) || (isExperiencePortal && isBuptCas)
  const selectCourse = async (_, next) => {
    if (String(next) === String(courseId)) return
    setCourseId(next)
    setSwitching(true)
    setError('')
    try {
      await loadDetail(next)
      fatalNotifiedRef.current = false
    } catch (loadError) {
      const message = loadError.message || '加载课程入口失败'
      setError(message)
      notifyFatal(message)
    } finally {
      setSwitching(false)
    }
  }

  const brandStyle = useMemo(
    () => buildAccentThemeStyle(currentUser?.branding, 'portal'),
    [currentUser?.branding],
  )

  const setPathPresentationPersist = (next) => {
    setPathPresentation(next)
    try {
      window.localStorage?.setItem(PATH_PRESENTATION_KEY, next)
    } catch (_) { /* ignore */ }
  }

  let mainContent
  if (loading) {
    mainContent = (
      <div className="course-portal-loading">
        <CircularProgress size={28} />
        <span>正在加载我的课程…</span>
      </div>
    )
  } else if (error) {
    mainContent = (
      <div className="course-portal-loading">
        <div className="portal-inline-alert is-error">{error}</div>
        <span>将回落到综合训练视图…</span>
        <button
          type="button"
          className="portal-retry-btn"
          onClick={() => {
            fatalNotifiedRef.current = false
            refresh(courseId)
          }}
        >
          重试课程入口
        </button>
      </div>
    )
  } else {
    mainContent = (
      <div className="course-portal-shell">
        <div className="portal-sticky-top">
          <CourseSwitcher courses={courses} courseId={courseId} onChange={selectCourse} />

          {isTrial ? (
            <TrialBanner
              showJoinButton={isBuptCas}
              onOpenCourseManagement={onOpenCourseManagement}
            />
          ) : (
            <header className="portal-heading">
              <div className="portal-heading-copy">
                <span className="portal-eyebrow">MY COURSE</span>
                <h1 className="portal-heading-title">{portal?.course?.name || '我的课程'}</h1>
                <p className="portal-heading-welcome">
                  {portal?.course?.welcomeText || '从学习路径开始你的训练，按教师编排的顺序推进。'}
                </p>
                {isExperiencePortal ? (
                  <p className="portal-heading-experience-note">
                    当前为体验视图（未加入正式课程），内容与试用课程一致
                    {canJoinCourse ? '；可点击右侧加入课程。' : '。'}
                  </p>
                ) : null}
              </div>
              <div className="portal-heading-actions">
                {canJoinCourse ? (
                  <button
                    type="button"
                    className="portal-join-course-btn"
                    onClick={onOpenCourseManagement}
                  >
                    加入课程
                  </button>
                ) : null}
                <div className="view-switcher" role="tablist" aria-label="视图切换">
                  <button
                    type="button"
                    className={`view-switcher-btn${view === 'path' ? ' active' : ''}`}
                    onClick={() => setView('path')}
                  >
                    学习路径
                  </button>
                  <button
                    type="button"
                    className={`view-switcher-btn${view === 'category' ? ' active' : ''}`}
                    onClick={() => setView('category')}
                  >
                    按类别
                  </button>
                </div>
                {view === 'path' ? (
                  <div className="path-presentation-switcher" role="tablist" aria-label="路径呈现">
                    <button
                      type="button"
                      className={`path-presentation-btn${pathPresentation === 'map' ? ' active' : ''}`}
                      onClick={() => setPathPresentationPersist('map')}
                    >
                      地图
                    </button>
                    <button
                      type="button"
                      className={`path-presentation-btn${pathPresentation === 'list' ? ' active' : ''}`}
                      onClick={() => setPathPresentationPersist('list')}
                    >
                      列表
                    </button>
                  </div>
                ) : null}
              </div>
            </header>
          )}
        </div>

        <div className="portal-scroll-body">
          {view === 'category' || isTrial ? (
            !portal?.items?.length ? (
              <div className="portal-empty">
                暂无可展示的已授权资源。你仍可通过顶部训练入口查看全部可用资源。
              </div>
            ) : (
              <CategoryView groups={groupedByCategory} onLaunch={onLaunch} />
            )
          ) : isPathZeroQuests ? (
            <div className="portal-empty">
              教师尚未编排学习路径关卡。可先切换到「按类别」自由练习，或等待教师布置作业后回来闯关。
            </div>
          ) : !pathItems.length ? (
            <div className="portal-empty">
              教师暂未配置学习路径，也没有可展示的已授权资源。你仍可通过顶部训练入口查看全部可用资源。
            </div>
          ) : pathPresentation === 'list' ? (
            <PathView
              sections={groupedByPath}
              timelineUnit={portal?.course?.timelineUnit}
              onLaunch={onLaunch}
              onOpenHomework={onOpenHomework}
              onOpenEvaluation={onOpenEvaluation}
            />
          ) : (
            <PathMapView
              sections={groupedByPath}
              timelineUnit={portal?.course?.timelineUnit}
              courseId={courseId}
              courseName={portal?.course?.name}
              onLaunch={onLaunch}
              onOpenHomework={onOpenHomework}
              onOpenEvaluation={onOpenEvaluation}
              onOpenGrowth={onOpenGrowth}
            />
          )}

          {!isTrial && (
            <PortalSummary
              courseId={courseId}
              courseName={portal?.course?.name}
              studentId={studentId}
              homework={portal?.homework || []}
              evaluation={portal?.evaluation || {}}
              timelineUnit={portal?.course?.timelineUnit}
              onOpenHomework={onOpenHomework}
              onOpenGrowth={onOpenGrowth}
            />
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`course-portal${switching ? ' is-switching' : ''}`}
      style={brandStyle}
    >
      <div className="course-portal-layout">
        <div className="course-portal-main">
          {mainContent}
        </div>
        <PortalGuidePanel
          agent={guideAgent}
          user={currentUser}
          expertServiceUrl={guideExpertServiceUrl}
          welcomeContentOverride={guideWelcomeContent}
          guideDocLinks={guideDocLinks}
          onA2UIAction={onGuideA2UIAction}
          spotlight={guideSpotlight}
          focusToken={guideFocusToken}
        />
      </div>
    </div>
  )
}
